import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Legend, LineChart, Line
} from 'recharts';
import { 
  AlertTriangle, Package, Truck, Activity, TrendingUp, 
  TrendingDown, Shield, AlertCircle, CheckCircle2 
} from 'lucide-react';
import THRDistrictDetailModal from '../../components/THRDistrictDetailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const THRDashboardEnhanced = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // State for each tab's data
  const [overviewData, setOverviewData] = useState(null);
  const [districtData, setDistrictData] = useState([]);
  const [hcmData, setHcmData] = useState(null);
  const [leakageData, setLeakageData] = useState(null);
  const [otpData, setOtpData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      
      // Load all data in parallel
      const [overview, districts, hcm, leakage, otp, comparison] = await Promise.all([
        axios.get(`${API_URL}/api/dashboards/thr/overview`),
        axios.get(`${API_URL}/api/dashboards/thr/district-delivery?limit=50`),
        axios.get(`${API_URL}/api/dashboards/hcm/overview`),
        axios.get(`${API_URL}/api/dashboards/thr/leakage-detection?threshold=80`),
        axios.get(`${API_URL}/api/dashboards/thr/otp-authentication`),
        axios.get(`${API_URL}/api/dashboards/thr/ration-comparison`)
      ]);

      setOverviewData(overview.data);
      setDistrictData(districts.data);
      setHcmData(hcm.data);
      setLeakageData(leakage.data);
      setOtpData(otp.data);
      setComparisonData(comparison.data);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setLoading(false);
    }
  };

  // Reusable KPI Card Component
  const KPICard = ({ title, value, unit, icon: Icon, color, trend, subtitle }) => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-full`} style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-6 h-6" style={{ color }} />
          </div>
          {trend && (
            <div className={`flex items-center gap-1 text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <h3 className="text-sm font-medium text-gray-600 mb-2">{title}</h3>
        <p className="text-3xl font-bold" style={{ color }}>{value}{unit}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading THR Dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Tab 1: Overview
  const OverviewTab = () => {
    if (!overviewData) return <div>No data available</div>;

    const chartData = [
      { name: 'Balshakti', value: overviewData.balshakti.delivery_pct, fill: '#ef4444' },
      { name: 'Matru Shakti', value: overviewData.matru.delivery_pct, fill: '#f59e0b' },
      { name: 'Purna Shakti', value: overviewData.purna.delivery_pct, fill: '#22c55e' }
    ];

    return (
      <div className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            title="Balshakti (500mg) Delivery"
            value={overviewData.balshakti.delivery_pct}
            unit="%"
            icon={Package}
            color="#ef4444"
            subtitle={`${overviewData.balshakti.delivered.toLocaleString()} / ${overviewData.balshakti.scheduled.toLocaleString()}`}
          />
          <KPICard
            title="Matru Shakti (1kg) Delivery"
            value={overviewData.matru.delivery_pct}
            unit="%"
            icon={Package}
            color="#f59e0b"
            subtitle={`${overviewData.matru.delivered.toLocaleString()} / ${overviewData.matru.scheduled.toLocaleString()}`}
          />
          <KPICard
            title="Purna Shakti (1kg) Delivery"
            value={overviewData.purna.delivery_pct}
            unit="%"
            icon={Package}
            color="#22c55e"
            subtitle={`${overviewData.purna.delivered.toLocaleString()} / ${overviewData.purna.scheduled.toLocaleString()}`}
          />
          <KPICard
            title="Districts Covered"
            value={overviewData.total_districts}
            unit=""
            icon={Activity}
            color="#3b82f6"
            subtitle={`${overviewData.total_anganwadis.toLocaleString()} AWCs`}
          />
        </div>

        {/* Performance Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Multi-Programme Performance Comparison</CardTitle>
            <CardDescription>THR Delivery Performance Across All Ration Types</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart 
                data={chartData}
                margin={{ top: 20, right: 30, left: 50, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  label={{ value: 'Delivery (%)', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: 12 }}
                  width={50}
                />
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Delivery Rate']}
                  contentStyle={{ fontSize: '12px' }}
                />
                <Bar dataKey="value" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* OTP Authentication Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="pt-6 text-center">
              <Shield className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-600">{overviewData.balshakti.otp_pct}%</p>
              <p className="text-sm text-gray-600">Balshakti OTP Verification</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <Shield className="w-8 h-8 text-orange-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-orange-600">{overviewData.matru.otp_pct}%</p>
              <p className="text-sm text-gray-600">Matru Shakti OTP Verification</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <Shield className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-600">{overviewData.purna.otp_pct}%</p>
              <p className="text-sm text-gray-600">Purna Shakti OTP Verification</p>
            </CardContent>
          </Card>
        </div>

        {/* Critical Alerts */}
        <Card className="border-red-500 border-2">
          <CardHeader>
            <CardTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Critical Performance Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {overviewData.balshakti.delivery_pct < 80 && (
                <div className="p-4 bg-red-50 rounded-lg">
                  <h4 className="font-semibold text-red-900 mb-2">Balshakti Distribution Below Threshold</h4>
                  <p className="text-sm text-red-700">
                    Only {overviewData.balshakti.delivery_pct}% delivery rate - {overviewData.districts_below_threshold} districts need immediate intervention
                  </p>
                </div>
              )}
              {overviewData.matru.delivery_pct < 80 && (
                <div className="p-4 bg-orange-50 rounded-lg">
                  <h4 className="font-semibold text-orange-900 mb-2">Matru Shakti Supply Chain Gap</h4>
                  <p className="text-sm text-orange-700">
                    Only {overviewData.matru.delivery_pct}% completion - {(overviewData.matru.scheduled - overviewData.matru.delivered).toLocaleString()} units gap
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Tab 2: THR Deep Dive
  const THRDeepDiveTab = () => {
    if (!districtData || districtData.length === 0) return <div>No district data available</div>;

    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>District-Wise THR Delivery Performance</CardTitle>
            <CardDescription>Detailed breakdown of all 3 ration types across {districtData.length} districts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">District</th>
                    <th className="px-4 py-3 text-center font-semibold">AWCs</th>
                    <th className="px-4 py-3 text-center font-semibold">Balshakti %</th>
                    <th className="px-4 py-3 text-center font-semibold">Matru %</th>
                    <th className="px-4 py-3 text-center font-semibold">Purna %</th>
                    <th className="px-4 py-3 text-center font-semibold">Avg Performance</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {districtData.map((district, idx) => {
                    const avgPerf = (
                      (district.balshakti.delivery_pct + district.matru.delivery_pct + district.purna.delivery_pct) / 3
                    ).toFixed(1);
                    const perfColor = avgPerf >= 80 ? 'text-green-600' : avgPerf >= 50 ? 'text-orange-600' : 'text-red-600';
                    
                    return (
                      <tr 
                        key={idx} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setSelectedDistrict(district);
                          setIsModalOpen(true);
                        }}
                      >
                        <td className="px-4 py-3 font-medium">{district.district}</td>
                        <td className="px-4 py-3 text-center">{district.anganwadi_count}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={district.balshakti.delivery_pct >= 80 ? 'text-green-600 font-semibold' : 'text-red-600'}>
                            {district.balshakti.delivery_pct}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={district.matru.delivery_pct >= 80 ? 'text-green-600 font-semibold' : 'text-red-600'}>
                            {district.matru.delivery_pct}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={district.purna.delivery_pct >= 80 ? 'text-green-600 font-semibold' : 'text-red-600'}>
                            {district.purna.delivery_pct}%
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-center font-bold ${perfColor}`}>
                          {avgPerf}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Tab 3: HCM & MMY
  const HCMMMYTab = () => {
    if (!hcmData) return <div>No HCM data available</div>;

    const supplyChainData = [
      { stage: 'Allocated', value: hcmData.total_allocated, fill: '#3b82f6' },
      { stage: 'Issued', value: hcmData.total_issued, fill: '#f59e0b' },
      { stage: 'Sale', value: hcmData.total_sale, fill: '#22c55e' }
    ];

    return (
      <div className="space-y-6">
        {/* HCM KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            title="Total Allocated (KG)"
            value={(hcmData.total_allocated / 1000).toFixed(1)}
            unit="K"
            icon={Package}
            color="#3b82f6"
          />
          <KPICard
            title="Total Issued (KG)"
            value={(hcmData.total_issued / 1000).toFixed(1)}
            unit="K"
            icon={Truck}
            color="#f59e0b"
            subtitle={`${hcmData.issue_rate}% issue rate`}
          />
          <KPICard
            title="Total Sale (KG)"
            value={(hcmData.total_sale / 1000).toFixed(1)}
            unit="K"
            icon={Activity}
            color="#22c55e"
            subtitle={`${hcmData.sale_rate}% sale rate`}
          />
          <KPICard
            title="Avg Sale Performance"
            value={hcmData.avg_sale_pct}
            unit="%"
            icon={TrendingUp}
            color={hcmData.avg_sale_pct >= 80 ? '#22c55e' : '#ef4444'}
          />
        </div>

        {/* Supply Chain Visualization */}
        <Card>
          <CardHeader>
            <CardTitle>HCM Supply Chain Flow</CardTitle>
            <CardDescription>Fortified Rice: Allocation → Issue → Sale Process</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={supplyChainData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="stage" />
                <YAxis label={{ value: 'Quantity (KG)', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value) => [`${value.toLocaleString()} KG`, 'Quantity']} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gap Analysis */}
        <Card className="border-orange-500 border-2">
          <CardHeader>
            <CardTitle className="text-orange-600 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Supply Chain Gap Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-orange-50 rounded-lg">
                <h4 className="font-semibold text-orange-900 mb-2">Allocation to Issue Gap</h4>
                <p className="text-2xl font-bold text-orange-600">
                  {((hcmData.total_allocated - hcmData.total_issued) / 1000).toFixed(1)}K KG
                </p>
                <p className="text-sm text-orange-700 mt-1">
                  {(100 - hcmData.issue_rate).toFixed(1)}% of allocated stock not issued
                </p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <h4 className="font-semibold text-red-900 mb-2">Issue to Sale Gap</h4>
                <p className="text-2xl font-bold text-red-600">
                  {((hcmData.total_issued - hcmData.total_sale) / 1000).toFixed(1)}K KG
                </p>
                <p className="text-sm text-red-700 mt-1">
                  {(100 - hcmData.sale_rate).toFixed(1)}% of issued stock not sold
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Districts Below Threshold */}
        {hcmData.districts_below_threshold > 0 && (
          <Card className="border-red-500 border-2">
            <CardHeader>
              <CardTitle className="text-red-600">Critical: Low Performance Districts</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg">
                <span className="font-bold text-red-600">{hcmData.districts_below_threshold}</span> districts 
                have HCM sale performance below 50% threshold
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // Tab 4: Red Flags & Leakages
  const RedFlagsTab = () => {
    if (!leakageData) return <div>No leakage data available</div>;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-red-500 border-2">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-3" />
              <p className="text-3xl font-bold text-red-600">{leakageData.total_districts_with_alerts}</p>
              <p className="text-sm text-gray-600 mt-1">Districts with Alerts</p>
              <p className="text-xs text-red-600 mt-2">Below {leakageData.threshold}% threshold</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6 text-center">
              <Package className="w-12 h-12 text-orange-600 mx-auto mb-3" />
              <p className="text-3xl font-bold text-orange-600">
                {leakageData.alerts.reduce((sum, d) => sum + d.alert_count, 0)}
              </p>
              <p className="text-sm text-gray-600 mt-1">Total Leakage Points</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <p className="text-3xl font-bold text-green-600">
                {districtData.length - leakageData.total_districts_with_alerts}
              </p>
              <p className="text-sm text-gray-600 mt-1">Districts Above Threshold</p>
            </CardContent>
          </Card>
        </div>

        {/* Leakage Details */}
        <Card>
          <CardHeader>
            <CardTitle>District-Wise Leakage Detection</CardTitle>
            <CardDescription>Districts with delivery below {leakageData.threshold}% threshold</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {leakageData.alerts.slice(0, 15).map((district, idx) => (
                <div key={idx} className="border-l-4 border-red-500 pl-4 py-2 bg-red-50 rounded">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-bold text-red-900">{district.district}</h4>
                    <span className="bg-red-600 text-white px-2 py-1 rounded text-xs">
                      {district.alert_count} Alert{district.alert_count > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {district.alerts.map((alert, alertIdx) => (
                      <div key={alertIdx} className="text-sm">
                        <span className="font-semibold text-red-800">{alert.ration_type}:</span>
                        <span className="text-red-700 ml-2">
                          {alert.delivery_pct}% delivery ({alert.delivered.toLocaleString()} / {alert.scheduled.toLocaleString()})
                        </span>
                        <span className="text-red-600 ml-2 font-medium">
                          Gap: {alert.gap.toLocaleString()} units
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Tab 5: OTP Authentication
  const OTPAuthTab = () => {
    if (!otpData) return <div>No OTP data available</div>;

    const otpSummaryData = [
      { category: 'High (≥80%)', count: otpData.summary.high_otp, fill: '#22c55e' },
      { category: 'Medium (50-79%)', count: otpData.summary.medium_otp, fill: '#f59e0b' },
      { category: 'Low (<50%)', count: otpData.summary.low_otp, fill: '#ef4444' }
    ];

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-green-500 border-2">
            <CardContent className="pt-6 text-center">
              <Shield className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <p className="text-3xl font-bold text-green-600">{otpData.summary.high_otp}</p>
              <p className="text-sm text-gray-600 mt-1">High OTP Usage (≥80%)</p>
            </CardContent>
          </Card>
          
          <Card className="border-orange-500 border-2">
            <CardContent className="pt-6 text-center">
              <Shield className="w-12 h-12 text-orange-600 mx-auto mb-3" />
              <p className="text-3xl font-bold text-orange-600">{otpData.summary.medium_otp}</p>
              <p className="text-sm text-gray-600 mt-1">Medium OTP Usage (50-79%)</p>
            </CardContent>
          </Card>

          <Card className="border-red-500 border-2">
            <CardContent className="pt-6 text-center">
              <Shield className="w-12 h-12 text-red-600 mx-auto mb-3" />
              <p className="text-3xl font-bold text-red-600">{otpData.summary.low_otp}</p>
              <p className="text-sm text-gray-600 mt-1">Low OTP Usage (&lt;50%)</p>
            </CardContent>
          </Card>
        </div>

        {/* OTP Distribution Chart */}
        <Card>
          <CardHeader>
            <CardTitle>OTP Verification Distribution</CardTitle>
            <CardDescription>District categorization by digital accountability level</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <Pie
                  data={otpSummaryData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ category, count, percent }) => {
                    // Only show label if percentage is >= 5% to avoid overlapping small slices
                    // For larger slices, show shortened category name
                    if (percent * 100 >= 5) {
                      const shortCategory = category.split(' ')[0]; // "High", "Medium", or "Low"
                      return `${shortCategory}: ${count}`;
                    }
                    return '';
                  }}
                  outerRadius={120}
                  innerRadius={40}
                  dataKey="count"
                >
                  {otpSummaryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name, props) => {
                    return [`${props.payload.category}: ${value}`, 'Count'];
                  }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '30px' }}
                  iconSize={14}
                  style={{ fontSize: '13px' }}
                  formatter={(value, entry) => {
                    const data = otpSummaryData.find(d => d.fill === entry.color);
                    return data ? `${data.category}: ${data.count}` : value;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* District Details */}
        <Card>
          <CardHeader>
            <CardTitle>District-Wise OTP Authentication Details</CardTitle>
            <CardDescription>Digital verification coverage across all ration types</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">District</th>
                    <th className="px-4 py-3 text-center font-semibold">Balshakti OTP</th>
                    <th className="px-4 py-3 text-center font-semibold">Matru OTP</th>
                    <th className="px-4 py-3 text-center font-semibold">Purna OTP</th>
                    <th className="px-4 py-3 text-center font-semibold">Avg OTP %</th>
                    <th className="px-4 py-3 text-center font-semibold">Rating</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {otpData.districts.slice(0, 20).map((district, idx) => {
                    const ratingColor = district.rating === 'High' ? 'text-green-600' : 
                                       district.rating === 'Medium' ? 'text-orange-600' : 'text-red-600';
                    
                    return (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{district.district}</td>
                        <td className="px-4 py-3 text-center">{district.balshakti_otp}%</td>
                        <td className="px-4 py-3 text-center">{district.matru_otp}%</td>
                        <td className="px-4 py-3 text-center">{district.purna_otp}%</td>
                        <td className="px-4 py-3 text-center font-bold">{district.avg_otp_pct}%</td>
                        <td className={`px-4 py-3 text-center font-bold ${ratingColor}`}>
                          {district.rating}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Tab 6: Ration Comparison
  const RationComparisonTab = () => {
    if (!comparisonData) return <div>No comparison data available</div>;

    const comparisonChartData = comparisonData.comparison.map(item => ({
      name: item.ration_type,
      'Delivery %': item.avg_delivery_pct,
      'Above 80%': item.districts_above_80,
      'Below 80%': item.districts_below_80
    }));

    return (
      <div className="space-y-6">
        {/* Performance Comparison */}
        <Card>
          <CardHeader>
            <CardTitle>Ration Type Performance Comparison</CardTitle>
            <CardDescription>Side-by-side analysis of all 3 THR ration types</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart 
                data={comparisonChartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  width={60}
                />
                <Tooltip 
                  contentStyle={{ fontSize: '12px' }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '10px' }}
                  iconSize={12}
                  style={{ fontSize: '12px' }}
                />
                <Bar dataKey="Delivery %" fill="#3b82f6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* District Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>District Performance Distribution</CardTitle>
            <CardDescription>Number of districts above/below 80% threshold</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={comparisonChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="Above 80%" fill="#22c55e" radius={[8, 8, 0, 0]} />
                <Bar dataKey="Below 80%" fill="#ef4444" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Detailed Comparison Table */}
        <Card>
          <CardHeader>
            <CardTitle>Detailed Performance Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Ration Type</th>
                    <th className="px-4 py-3 text-center font-semibold">Avg Delivery %</th>
                    <th className="px-4 py-3 text-center font-semibold">Districts ≥80%</th>
                    <th className="px-4 py-3 text-center font-semibold">Districts &lt;80%</th>
                    <th className="px-4 py-3 text-center font-semibold">Performance</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {comparisonData.comparison.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{item.ration_type}</td>
                      <td className="px-4 py-3 text-center font-bold">
                        <span className={item.avg_delivery_pct >= 80 ? 'text-green-600' : 'text-red-600'}>
                          {item.avg_delivery_pct}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-green-600 font-semibold">
                        {item.districts_above_80}
                      </td>
                      <td className="px-4 py-3 text-center text-red-600 font-semibold">
                        {item.districts_below_80}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.avg_delivery_pct >= 80 ? (
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">
                            Excellent
                          </span>
                        ) : item.avg_delivery_pct >= 50 ? (
                          <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs font-semibold">
                            Needs Improvement
                          </span>
                        ) : (
                          <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-semibold">
                            Critical
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Summary Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Total Districts Analyzed</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-blue-600">{comparisonData.total_districts}</p>
              <p className="text-sm text-gray-600 mt-2">Across Gujarat State</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Overall Performance Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {comparisonData.comparison.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-sm font-medium">{item.ration_type}:</span>
                    <span className={`text-sm font-bold ${item.avg_delivery_pct >= 80 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.avg_delivery_pct >= 80 ? '✓ On Track' : '⚠ Action Needed'}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="mb-8 pt-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              THR / HCM / MMY Distribution Dashboard
            </h1>
            <p className="text-gray-600">
              Comprehensive monitoring of Take Home Ration, Hot Cooked Meal & MMY programmes
            </p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="thr-dive">THR Deep Dive</TabsTrigger>
              <TabsTrigger value="hcm-mmy">HCM & MMY</TabsTrigger>
              <TabsTrigger value="red-flags">Red Flags</TabsTrigger>
              <TabsTrigger value="otp-auth">OTP Auth</TabsTrigger>
              <TabsTrigger value="comparison">Comparison</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <OverviewTab />
            </TabsContent>

            <TabsContent value="thr-dive">
              <THRDeepDiveTab />
            </TabsContent>

            <TabsContent value="hcm-mmy">
              <HCMMMYTab />
            </TabsContent>

            <TabsContent value="red-flags">
              <RedFlagsTab />
            </TabsContent>

            <TabsContent value="otp-auth">
              <OTPAuthTab />
            </TabsContent>

            <TabsContent value="comparison">
              <RationComparisonTab />
            </TabsContent>
          </Tabs>

        </div>
      </div>

      {/* District Detail Modal */}
      <THRDistrictDetailModal
        district={selectedDistrict}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedDistrict(null);
        }}
        reportingPeriod={overviewData?.reporting_period || null}
        dashboardType="thr"
      />
    </div>
  );
};

export default THRDashboardEnhanced;
